// Shared reducer for the two rule slices.
//
// Commission rules and clawback rules have the same shape — a paginated list, a
// detail fetch that merges into the cache, create/update writes, and a validate
// call whose results and per-formula errors are displayed next to the builder.
// Writing that twice would mean fixing every bug twice, so both slices are built
// from this factory and differ only in their action types and extras.

import {
  createListReducer,
  createWriteReducer,
  initialListState,
  initialWriteState
} from './listReducerFactory.es6';

export function initialValidationState() {
  return {
    loading: false,
    // Per-formula results from the service. Amounts come back IN CENTS.
    results: null,
    // [{ message, formula, name, policy_periods }]
    errors: [],
    error: null,
    ranAt: null
  };
}

// Reads the validate response. The two endpoints do NOT agree on the key:
//
//   clawback    { results: {...},                 errors: [] }
//   commission  { results_amount_in_cents: {...}, errors: [] }
//
// (clawback-rule.controller.ts returns `results`, commission-rule.controller.ts
// returns `results_amount_in_cents`.) Reading only `results` silently dropped
// every commission result: `errors` is present, so the branch matched and
// results came back null — "Test the formulas" on a commission rule displayed
// nothing at all, with no error to explain why. Captured from the live endpoint.
//
// Both keys plus a bare results object are accepted rather than assuming one.
export function readValidationResponse(payload) {
  if (!payload) return { results: null, errors: [] };

  var named = payload.results !== undefined
    ? payload.results
    : payload.results_amount_in_cents;

  if (named !== undefined || payload.errors !== undefined) {
    return {
      results: named === undefined ? null : named,
      errors: Array.isArray(payload.errors) ? payload.errors : []
    };
  }
  // An undocumented shape: treat the whole body as the results map.
  return { results: payload, errors: [] };
}

// types:
//   list:     { request, success, failure }
//   detail:   { request, success, failure }
//   create:   { request, success, failure, reset, resetError }
//   update:   { request, success, failure, reset, resetError }
//   validate: { request, success, failure, reset }
//   setToEdit / resetToEdit
//
// extraReducer(state, action) is called last and may return a new state; used
// by the clawback slice for its commission-rule symbols cache.
export function createRulesReducer(types, options) {
  var opts = options || {};

  var listReducer = createListReducer(
    {
      request: types.list.request,
      success: types.list.success,
      failure: types.list.failure,
      detailSuccess: types.detail.success
    },
    { keepRowsWhileLoading: true }
  );

  var createReducer = createWriteReducer({
    request: types.create.request,
    success: types.create.success,
    failure: types.create.failure,
    reset: types.create.reset
  });

  var updateReducer = createWriteReducer({
    request: types.update.request,
    success: types.update.success,
    failure: types.update.failure,
    reset: types.update.reset
  });

  function initial() {
    return Object.assign(
      {
        list: initialListState(),
        listParams: {},
        detailLoading: false,
        detailError: null,
        ruleToEdit: null,
        create: initialWriteState(),
        update: initialWriteState(),
        validation: initialValidationState()
      },
      opts.extraInitialState || {}
    );
  }

  function reducer(state, action) {
    if (state === undefined) state = initial();
    if (!action || !action.type) return state;

    var next = state;

    var list = listReducer(state.list, action);
    if (list !== state.list) {
      next = Object.assign({}, next, { list: list });
    }

    var created = createReducer(next.create, action);
    if (created !== next.create) {
      next = Object.assign({}, next, { create: created });
    }

    var updated = updateReducer(next.update, action);
    if (updated !== next.update) {
      next = Object.assign({}, next, { update: updated });
    }

    switch (action.type) {
      case types.list.request:
        next = Object.assign({}, next, { listParams: action.payload || {} });
        break;

      case types.detail.request:
        next = Object.assign({}, next, {
          detailLoading: true,
          detailError: null
        });
        break;

      case types.detail.success:
        next = Object.assign({}, next, {
          detailLoading: false,
          detailError: null
        });
        break;

      case types.detail.failure:
        next = Object.assign({}, next, {
          detailLoading: false,
          detailError: action.payload || { message: 'Could not load this rule' }
        });
        break;

      case types.validate.request:
        next = Object.assign({}, next, {
          validation: Object.assign({}, initialValidationState(), {
            loading: true
          })
        });
        break;

      case types.validate.success: {
        var read = readValidationResponse(action.payload);
        next = Object.assign({}, next, {
          validation: {
            loading: false,
            results: read.results,
            errors: read.errors,
            error: null,
            // A counter rather than a timestamp: Date.now() in a reducer makes
            // state untestable and non-deterministic.
            ranAt: (state.validation.ranAt || 0) + 1
          }
        });
        break;
      }

      case types.validate.failure:
        next = Object.assign({}, next, {
          validation: Object.assign({}, initialValidationState(), {
            error: action.payload || { message: 'Validation failed' },
            ranAt: (state.validation.ranAt || 0) + 1
          })
        });
        break;

      default:
        break;
    }

    if (types.validate.reset && action.type === types.validate.reset) {
      next = Object.assign({}, next, {
        validation: initialValidationState()
      });
    }

    if (types.setToEdit && action.type === types.setToEdit) {
      next = Object.assign({}, next, { ruleToEdit: action.payload });
    }

    if (types.resetToEdit && action.type === types.resetToEdit) {
      next = Object.assign({}, next, { ruleToEdit: null });
    }

    // Error-only resets, which leave any success flag alone.
    var errorResets = [
      ['create', types.create.resetError],
      ['update', types.update.resetError]
    ];
    for (var i = 0; i < errorResets.length; i += 1) {
      var name = errorResets[i][0];
      if (errorResets[i][1] && action.type === errorResets[i][1]) {
        var patch = {};
        patch[name] = Object.assign({}, next[name], { error: null });
        next = Object.assign({}, next, patch);
      }
    }

    if (opts.extraReducer) {
      next = opts.extraReducer(next, action) || next;
    }

    return next;
  }

  reducer.initialState = initial;
  return reducer;
}

// Matching selectors, so neither slice hand-writes them.
export function createRulesSelectors(sliceName) {
  function slice(state) {
    return state[sliceName] || {};
  }

  function list(state) {
    return slice(state).list || { byGuid: {}, guids: [], pagination: {} };
  }

  return {
    slice: slice,
    rows: function(state) {
      var current = list(state);
      return (current.guids || [])
        .map(function(guid) { return (current.byGuid || {})[guid]; })
        .filter(Boolean);
    },
    byGuid: function(state) { return list(state).byGuid || {}; },
    ruleByGuid: function(state, guid) {
      return guid ? list(state).byGuid[guid] || null : null;
    },
    pagination: function(state) {
      return list(state).pagination || { page: 1, size: 10, total: 0 };
    },
    loading: function(state) { return !!list(state).loading; },
    error: function(state) { return list(state).error || null; },
    listParams: function(state) { return slice(state).listParams || {}; },
    detailLoading: function(state) { return !!slice(state).detailLoading; },
    detailError: function(state) { return slice(state).detailError || null; },
    createState: function(state) {
      return slice(state).create || initialWriteState();
    },
    updateState: function(state) {
      return slice(state).update || initialWriteState();
    },
    validation: function(state) {
      return slice(state).validation || initialValidationState();
    }
  };
}
